import type { ChatMessageView } from '@steado/admin-contract/user-detail';
import { useQuery } from '@tanstack/react-query';

import { BadgeCell } from '@/components/table/cells';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/lib/auth/auth-context';
import { cn } from '@/lib/utils';

import { Empty, Panel, TabSkeleton } from '@/components/panel/panels';
import { TabError } from './TabError';
import { formatDateTime } from '@/lib/format';
import { userCoachQueryOptions, userTranscriptQueryOptions } from './user-detail-query';

interface CoachTabProps {
  readonly userId: string;
  readonly sessionId: string | undefined;
  readonly onOpenSession: (sessionId: string | undefined) => void;
}

/**
 * Sessions, memories, and one transcript at a time.
 *
 * The session list carries counts only — no message content — so opening this
 * tab does not pull every conversation the user has ever had. A transcript is
 * a second request, made when an operator picks a session, and the choice lives
 * in the URL so it can be linked to.
 */
export function CoachTab({ userId, sessionId, onOpenSession }: CoachTabProps) {
  const { api } = useAuth();
  const coach = useQuery(userCoachQueryOptions(api, userId, true));
  const transcript = useQuery(userTranscriptQueryOptions(api, userId, sessionId ?? null));

  if (coach.isPending) return <TabSkeleton rows={4} />;
  if (coach.isError) {
    return <TabError message={coach.error.message} onRetry={() => void coach.refetch()} />;
  }

  return (
    <div className="flex flex-col gap-3">
      <Panel
        title={
          coach.data.sessions.length === 1 ? '1 session' : `${String(coach.data.sessions.length)} sessions`
        }
        bleed
      >
        {coach.data.sessions.length === 0 ? (
          <div className="border-t border-border">
            <Empty title="No coach sessions" body="This user has not opened the coach tab." />
          </div>
        ) : (
          <ul>
            {coach.data.sessions.map((session) => {
              const isOpen = session.id === sessionId;

              return (
                <li
                  key={session.id}
                  className={cn('flex flex-col border-t border-border', isOpen && 'bg-secondary/40')}
                >
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => {
                      onOpenSession(isOpen ? undefined : session.id);
                    }}
                    className="flex w-full cursor-pointer items-center gap-2.5 px-[18px] py-2.5 text-left hover:bg-secondary"
                  >
                    <span className="shrink-0 font-mono text-[12.5px] text-muted-foreground">
                      {formatDateTime(session.startedAt)}
                    </span>
                    <span className="min-w-0 flex-1 text-[13px] text-foreground">
                      {session.messageCount === 1 ? '1 turn' : `${String(session.messageCount)} turns`}
                    </span>
                    <BadgeCell label={session.status} tone={session.status === 'ended' ? 'mute' : 'ok'} />
                  </button>

                  {isOpen ? (
                    <div className="mb-[15px] ml-[29px] border-l-2 border-border pr-[18px] pl-[15px]">
                      {transcript.isPending ? (
                        <TabSkeleton rows={3} />
                      ) : transcript.isError ? (
                        <TabError
                          message={transcript.error.message}
                          onRetry={() => void transcript.refetch()}
                        />
                      ) : (
                        <ol className="flex flex-col gap-3">
                          {transcript.data.messages.map((message) => (
                            <Turn key={message.id} message={message} />
                          ))}
                        </ol>
                      )}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      <Panel
        title={
          coach.data.memories.length === 1 ? '1 memory' : `${String(coach.data.memories.length)} memories`
        }
        description="Facts the extractor mined from ended sessions. Nothing is deleted: the coach reasons about what changed."
        bleed
      >
        {coach.data.memories.length === 0 ? (
          <div className="border-t border-border">
            <Empty
              title="No memories extracted"
              body="Facts are mined from sessions once they end. An open session is never extracted from."
            />
          </div>
        ) : (
          <ul>
            {coach.data.memories.map((memory) => (
              <li
                key={memory.id}
                className="flex items-baseline gap-3 border-t border-border px-[18px] py-2.5"
              >
                <span className="w-[104px] shrink-0 font-mono text-[12.5px] text-muted-foreground">
                  {formatDateTime(memory.createdAt)}
                </span>
                <span
                  className={cn(
                    'min-w-0 flex-1 text-[13px] leading-[1.55] text-pretty',
                    memory.supersededAt === null ? 'text-foreground' : 'text-muted-foreground line-through',
                  )}
                >
                  {memory.content}
                </span>
                {/* Superseded facts are kept, not deleted — the coach reasons about change. */}
                {memory.supersededAt === null ? null : <BadgeCell label="superseded" tone="mute" />}
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

const ROLE_LABELS: Readonly<Record<string, string>> = {
  user: 'User',
  assistant: 'Coach',
  tool: 'Tool',
};

function Turn({ message }: { readonly message: ChatMessageView }) {
  return (
    <li className="flex flex-col gap-1">
      <div className="flex items-baseline gap-2">
        <span className="text-xs font-semibold tracking-[0.06em] text-muted-foreground uppercase">
          {ROLE_LABELS[message.role] ?? message.role}
          {message.toolName === null ? '' : ` · ${message.toolName}`}
        </span>
        <span className="font-mono text-xs text-muted-foreground">{formatDateTime(message.createdAt)}</span>
      </div>

      <p
        className={cn(
          'text-sm leading-relaxed whitespace-pre-wrap',
          message.role === 'tool' ? 'font-mono text-[13px] text-muted-foreground' : 'text-foreground',
        )}
      >
        {message.content}
      </p>

      {message.toolCalls.length === 0 ? null : (
        <ul className="flex flex-wrap gap-1">
          {message.toolCalls.map((call, index) => (
            <li key={call.id ?? `${call.name}-${String(index)}`}>
              <Badge tone="outline" className="font-mono text-muted-foreground">
                {call.name}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
